import { useEffect, useImperativeHandle, useState } from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { Checkbox } from "./Checkbox";
import { type CheckboxDataProps, checkboxDataSchema } from "./Checkbox.schema";
import { CHECKBOX_TAG_NAME } from "./constants";

export interface CheckboxHandle {
  checked: (value?: boolean) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [CHECKBOX_TAG_NAME]: { props: CheckboxDataProps; handle: CheckboxHandle };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-checkbox": HTMLElement & CheckboxHandle;
  }
}

export function RWCCheckbox({
  ref,
  checked: initialChecked,
  disabled: initialDisabled,
  inputId,
  value,
  ...props
}: CheckboxDataProps & { ref?: Ref<CheckboxHandle> }) {
  const host = useIslandHost();

  const [checked, setChecked] = useState(initialChecked ?? false);
  const [disabled, setDisabled] = useState(initialDisabled ?? false);

  const handle: CheckboxHandle = {
    checked: (value = true) => setChecked(value),
    disabled: (value = true) => setDisabled(value),
  };

  useImperativeHandle(ref, () => handle, []);

  useEffect(() => {
    getElementInternals(host)?.setFormValue(checked ? (value ?? "on") : null);
  }, [host, checked, value]);

  return (
    <Checkbox
      {...props}
      value={value}
      id={inputId}
      checked={checked}
      onCheckedChange={setChecked}
      disabled={disabled}
    />
  );
}

componentManager.register(
  rwc(CHECKBOX_TAG_NAME, {
    component: RWCCheckbox,
    dataSchema: checkboxDataSchema,
    formAssociated: true,
  }),
);
